import { useState, useEffect } from "react";
import axios, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AlertCircle, Clock, Youtube, VideoIcon } from "lucide-react";
import BroadcastsList from "./BroadcastsList";
import VideoUploadPanel from "./VideoUploadPanel";
import StreamingDebugPanel from "./StreamingDebugPanel";

const Dashboard = ({ user, onLogout }) => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingBroadcasts, setFetchingBroadcasts] = useState(true);

  useEffect(() => { fetchBroadcasts(); }, []);

  const fetchBroadcasts = async () => {
    try {
      const response = await axios.get(`${API}/broadcasts`, {
        headers: { Authorization: `Bearer ${user.access_token}` }
      });
      setBroadcasts(response.data.broadcasts || []);
    } catch (error) {
      console.error('Failed to fetch broadcasts:', error);
      toast.error('Failed to load scheduled broadcasts');
    } finally {
      setFetchingBroadcasts(false);
    }
  };

  const handleDeleteBroadcast = async (broadcastId) => {
    try {
      await axios.delete(`${API}/broadcasts/${broadcastId}`, {
        headers: { Authorization: `Bearer ${user.access_token}` }
      });
      toast.success('Broadcast deleted successfully');
      setBroadcasts(prev => prev.filter(b => b.id !== broadcastId));
      await fetchBroadcasts();
    } catch (error) {
      console.error('Failed to delete broadcast:', error);
      toast.error('Failed to delete broadcast');
      fetchBroadcasts();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Youtube className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">YouTube Live Scheduler</h1>
              <p className="text-sm text-gray-600">Welcome, {user.name}</p>
            </div>
          </div>
          <Button variant="outline" onClick={onLogout} data-testid="logout-button">Logout</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="upload" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto">
            <TabsTrigger value="upload" data-testid="upload-tab">Upload Videos</TabsTrigger>
            <TabsTrigger value="manage" data-testid="manage-tab">Manage Broadcasts</TabsTrigger>
            <TabsTrigger value="debug" data-testid="debug-tab">Debug Stream</TabsTrigger>
          </TabsList>

          <TabsContent value="upload">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><VideoIcon className="w-5 h-5" />Upload Videos</CardTitle>
              </CardHeader>
              <CardContent>
                <VideoUploadPanel user={user} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="manage">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" />Scheduled Broadcasts</CardTitle>
              </CardHeader>
              <CardContent>
                {fetchingBroadcasts ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading broadcasts...</p>
                  </div>
                ) : (
                  <BroadcastsList broadcasts={broadcasts} onDelete={handleDeleteBroadcast} loading={loading} />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="debug">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><AlertCircle className="w-5 h-5" />Debug Streaming</CardTitle>
              </CardHeader>
              <CardContent>
                <StreamingDebugPanel user={user} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
